import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnInit } from '@angular/core';
import { ModalService } from '@src/shared/modal/modal.service';
import { endOfISOWeekYear, startOfISOWeekYear } from 'date-fns';
import { formatWooDate } from '@scripts/utils/date';
import { scrollTo } from '@scripts/utils/dom';
import { PriceList, PriceListService } from '@scripts/woo_services.module/PriceListService';
import { RoutingService } from '@scripts/woo_services.module/RoutingService';
import { DatePeriod } from '@scripts/woo_services.module/shared-types';

@Component({
  selector: 'manage-pricelists',
  templateUrl: './manage-pricelists.component.html',
  styleUrls: ['../../../shared/table.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class ManagePriceLists implements OnInit {
  @Input('headerPos') parentHeader: HTMLElement;

  readonly DATE_FORMAT = 'yyyy-MM-dd HH:mm';
  readonly getLabel: (priceList: PriceList) => string;
  readonly sortPriceLists: (a: PriceList, b: PriceList) => number;
  readonly show = {
    newPriceListPicker: false,
    newPriceListForm: false,
  };

  readonly tableHeaders = ['Period', 'Skapad', 'Uppdaterad', ''];
  loading: boolean = true;
  priceLists: PriceList[] = [];
  newPriceList: Partial<PriceList>;

  constructor(
    private priceListService: PriceListService,
    private modalService: ModalService,
    private routingService: RoutingService,
    private changeDetection: ChangeDetectorRef,
  ) {
    this.getLabel = this.priceListService.getLabel;
    this.sortPriceLists = this.priceListService.priceListSort;
    this.newPriceList = this.priceListService.getEmpty();
  }

  ngOnInit(): void {
    this.priceListService
      .getPriceLists()
      .then((lists: PriceList[]) => {
        return (this.priceLists = lists.sort(this.sortPriceLists));
      })
      .finally(() => {
        this.loading = false;
        this.changeDetection.markForCheck();
      });
  }

  showNew(): void {
    this.show.newPriceListPicker = true;
  }

  templatePricelistFrom(template: PriceList, year: number): void {
    const newPeriod = this.getPeriodForYear(year);
    this.newPriceList = {
      ...template,
      valid_from: formatWooDate(newPeriod.start),
      valid_to: formatWooDate(newPeriod.end),
    };
    delete this.newPriceList.id;
    this.show.newPriceListForm = true;
  }

  hideNew(): void {
    this.show.newPriceListPicker = false;
    this.show.newPriceListForm = false;
    scrollTo(this.parentHeader);
  }

  createPriceList(list: PriceList): void {
    this.priceListService
      .addPriceList(list)
      .then(() => {
        const msg = `Prislistan för ${this.getLabel(list)} har lagts till!`;
        this.modalService.openSuccess(msg);
        this.hideNew();
        this.routingService.navigate(['/products'], {
          queryParams: { tab: 'prislistor' },
          state: { deviceGroupEstimatesChanged: true },
        });
        this.changeDetection.markForCheck();
      })
      .catch((err) => this.modalService.openError(err?.message ?? 'Ett fel inträffade vid skapandet av prislistan'));

    this.priceListService.getPriceLists().then((lists) => (this.priceLists = lists.sort(this.sortPriceLists)));
  }

  private getPeriodForYear(year: number): DatePeriod {
    const end = endOfISOWeekYear(new Date(year, 0, 4));
    const start = startOfISOWeekYear(new Date(year, 0, 4));
    return {
      start: start,
      end: end,
    };
  }
}
